import { useCallback, useEffect, useRef, useState } from 'react'
import Utils from '@utils/utils'

const loadImage = (src) => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => resolve(img)
        img.onerror = reject
        img.src = src
    })
}

const canvasToObjectUrl = (canvas, mimeType = 'image/png', quality = 0.92) => {
    return new Promise((resolve) => {
        if (canvas.toBlob) {
            canvas.toBlob((blob) => {
                if (!blob) {
                    resolve(canvas.toDataURL(mimeType, quality))
                    return
                }
                resolve(URL.createObjectURL(blob))
            }, mimeType, quality)
            return
        }
        resolve(canvas.toDataURL(mimeType, quality))
    })
}

const useComposeLongImage = () => {
    const [loading, setLoading] = useState(false)
    const [longImageUrl, setLongImageUrl] = useState('')
    const [error, setError] = useState('')
    const objectUrlRef = useRef('')
    const composingRef = useRef(false)

    const clear = useCallback(() => {
        if (objectUrlRef.current && objectUrlRef.current.startsWith('blob:')) {
            URL.revokeObjectURL(objectUrlRef.current)
        }
        objectUrlRef.current = ''
        setLongImageUrl('')
        setError('')
    }, [])

    const compose = useCallback(async (rawUrls = [], options = {}) => {
        const {
            targetWidth = 1000,
            mimeType = 'image/png',
            quality = 0.92,
            useThumbnail = true,
            largeType = true,
            gap = 0,
            background = '#ffffff',
        } = options

        const urls = (rawUrls || []).filter(Boolean)
        if (!urls.length) {
            setError('没有可拼接的图片')
            return ''
        }
        if (composingRef.current) return ''

        composingRef.current = true
        setLoading(true)
        setError('')

        try {
            const finalUrls = useThumbnail
                ? urls.map((url) => Utils.getImgThumbnail(url, undefined, largeType))
                : urls
            const images = await Promise.all(finalUrls.map((url) => loadImage(url)))

            const imageHeights = images.map((img) => {
                const ratio = img.height / img.width || 1
                return Math.round(targetWidth * ratio)
            })
            const totalHeight = imageHeights.reduce((sum, h) => sum + h, 0) + gap * (images.length - 1)

            const canvas = document.createElement('canvas')
            canvas.width = targetWidth
            canvas.height = totalHeight
            const ctx = canvas.getContext('2d')
            if (!ctx) throw new Error('无法创建画布')

            ctx.fillStyle = background
            ctx.fillRect(0, 0, canvas.width, canvas.height)

            let currentY = 0
            images.forEach((img, index) => {
                const drawHeight = imageHeights[index]
                ctx.drawImage(img, 0, currentY, targetWidth, drawHeight)
                currentY += drawHeight + gap
            })

            if (objectUrlRef.current && objectUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(objectUrlRef.current)
            }
            const resultUrl = await canvasToObjectUrl(canvas, mimeType, quality)
            objectUrlRef.current = resultUrl
            setLongImageUrl(resultUrl)
            return resultUrl
        } catch (err) {
            setError(err?.message || '长图拼接失败')
            return ''
        } finally {
            setLoading(false)
            composingRef.current = false
        }
    }, [])

    useEffect(() => {
        return () => {
            if (objectUrlRef.current && objectUrlRef.current.startsWith('blob:')) {
                URL.revokeObjectURL(objectUrlRef.current)
            }
        }
    }, [])

    return {
        compose,
        clear,
        loading,
        longImageUrl,
        error,
    }
}

export default useComposeLongImage
